import { Injectable } from "@angular/core";
import { CurrentState } from "../store/current-state";
import { CommonState } from "../store/reducer";
import { HttpClient } from "@angular/common/http";
import {
  CountyWithPolicyNumber,
  LRPFinalizedEndorsementRequest,
  LRPPolicy,
  LRPPolicyRequest,
  LRPWatchListEndorsement,
  LRPWatchListQuote, PrfAssociation,
  PRFAssociationRequest,
  UserDetail
} from "../model";
import { County, LRPCommodity, LRPCommodityType, LRPEndorsementLength, LRPRequest, State } from "../model/LRPModel";
import { ServicesPRFCommon } from "./services-prf-common.service";
import { combineLatest, distinctUntilChanged, filter, map, Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { AdminService } from "./admin.service";
import { ErrorHandler } from "../error-handler.helper";
import { SelectUserAction } from "../store/admin";
import { LoadUserAction } from "../store/user";
import { Store } from "@ngrx/store";
import * as _ from "lodash";

@Injectable()
export class LRPService {
  private readonly LRP_URL = `/private/lrp/`;

  private readonly TARGET_WEIGHT_2_STEER = "810";
  private readonly TARGET_WEIGHT_2_HEIFER = "812";
  private readonly TARGET_WEIGHT_2_BRAHMAN = "814";
  private readonly TARGET_WEIGHT_2_DAIRY = "816";


  constructor(
    private currentState: CurrentState<CommonState>,
    protected http: HttpClient,
    private store: Store<CommonState>,
    private adminService: AdminService,
    private servicesCommon: ServicesPRFCommon,
  ) {
  }

  public loadAllCommodities(): Observable<LRPCommodity[]> {
    return this.servicesCommon.getAll<LRPCommodity>(this.LRP_URL + 'hargrove/commodities')
  }

  public loadAllEndorsementLengths(): Observable<LRPEndorsementLength[]> {
    return this.servicesCommon.getAll<LRPEndorsementLength>(this.LRP_URL + 'hargrove/endorsements/lengths')
  }

  public loadAllCommodityTypes(): Observable<LRPCommodityType[]> {
    return this.servicesCommon.getAll<LRPCommodityType>(this.LRP_URL + 'hargrove/commodities/types')
  }

  public loadAllStates(): Observable<State[]> {
    return this.servicesCommon.getAll<State>(this.LRP_URL + 'hargrove/states')
  }

  public sendActivePoliciesSummaryEmail(request: LRPRequest) {
    let url = this.servicesCommon.getUrl(`/private/lrp/${request.company}/policies/active/summary/email`);
    return this.http.post<number>(url, request, {headers: this.servicesCommon.getAuthHeaders()});
  }

  public sendPoliciesDetailsEmail(request: LRPRequest) {
    let url = this.servicesCommon.getUrl(`/private/lrp/${request.company}/policies/details/summary/email`);
    return this.http.post<number>(url, request, {headers: this.servicesCommon.getAuthHeaders()});
  }

  getLRPProducers(request: { company: 'hargrove' | 'aap'; primaryName: string }) {
    return this.servicesCommon.getAll<any>(`/private/lrp/${request.company}/producers/${request.primaryName}`);
  }

  loadAllCounties() {
    return this.servicesCommon.getAll<County>(this.LRP_URL + 'hargrove/counties')
  }


  loadAllLRPUsers(company) {
    return this.servicesCommon.getAll<UserDetail>(this.LRP_URL + company + '/users')
  }

  loadLRPPolicies(request: LRPPolicyRequest): Observable<LRPPolicy[]> {

    let queryParams = '?';

    let ids = this.getCommaSeparatedStringFromArray(request.associationIds);
    if (!!ids) {
      queryParams += 'associationIds=' + ids + '&';
    }

    let commCodes = this.getCommaSeparatedStringFromArray(request.commodityCodes);
    if (!!commCodes) {
      queryParams += 'commodityCodes=' + commCodes + '&';
    }

    let tc = this.getCommaSeparatedStringFromArray(request.typeCodes);
    if (!!tc) {
      queryParams += 'typeCodes=' + tc + '&';
    }

    let reinsuranceYears = this.getCommaSeparatedStringFromArray(request.years);
    if (!!reinsuranceYears) {
      queryParams += 'reinsuranceYears=' + reinsuranceYears + '&';
    }
    return this.servicesCommon.getAll<LRPPolicy>(`/private/lrp/${request.company}/endorsements/policies${queryParams}`);
  }

  loadAvailableCounties(request: LRPPolicyRequest): Observable<CountyWithPolicyNumber[]> {

    let queryParams = '?';

    let associationId = request.associationIds[0];
    if (!!associationId) {
      queryParams += 'associationId=' + associationId + '&';
    }

    let commCodes = this.getCommaSeparatedStringFromArray(request.commodityCodes);
    if (!!commCodes) {
      queryParams += 'commodityCodes=' + commCodes + '&';
    }

    let reinsuranceYear = request.year;
    if (!!reinsuranceYear) {
      queryParams += 'reinsuranceYear=' + reinsuranceYear + '&';
    }

    let stateCode = request.stateCode;
    if (!!stateCode) {
      queryParams += 'stateCode=' + stateCode + '&';
    }
    return this.servicesCommon.getAll<CountyWithPolicyNumber>(`/private/lrp/${request.company}/endorsements/counties/available${queryParams}`);
  }

  loadLRPEndingPolicies(request: LRPPolicyRequest): Observable<LRPPolicy[]> {
    let queryParams = '?';

    let userId = this.currentState.snapshot.user.id;
    queryParams += 'agentUserId=' + userId + '&';

    let commCodes = this.getCommaSeparatedStringFromArray(request.commodityCodes);
    if (!!commCodes) {
      queryParams += 'commodityCodes=' + commCodes + '&';
    }

    let tc = this.getCommaSeparatedStringFromArray(request.typeCodes);
    if (!!tc) {
      queryParams += 'typeCodes=' + tc + '&';
    }

    if (!!request.year) {
      queryParams += 'reinsuranceYear=' + request.year + '&';
    }

    if (!!request.month) {
      queryParams += 'month=' + request.month + '&';
    }

    return this.servicesCommon.getAll<LRPPolicy>(`/private/lrp/${request.company}/endorsements/ending${queryParams}`);
  }


  private getCommaSeparatedStringFromArray(array: number[] | string[]) {
    if (!array) {
      return null;
    }
    let result = '';
    array.forEach(ai => {
      result += ai + ',';
    })
    if (result.length > 0) {
      result = result.substring(0, result.length - 1);
      return result;
    }
    return null;
  }

  finalizeQuote(request: LRPRequest) {
    let userId = request.userId;
    if (!request.userId) {
      userId = this.currentState.snapshot.user.id;
    }
    let url = this.servicesCommon.getUrl(`/private/lrp/${request.company}/endorsements/finalize/user/${userId}`);
    return this.http.post<number>(url, request, {headers: this.servicesCommon.getAuthHeaders()});
  }

  submitFinalizedEndorsementRequest(request: LRPRequest) {
    let url = this.servicesCommon.getUrl(`/private/lrp/${request.company}/endorsements/requests/submit`);
    return this.http.post<number>(url, request, {headers: this.servicesCommon.getAuthHeaders()});
  }


  loadLatestEffectiveDate() {
    return this.servicesCommon.get<{ latestEffectiveDate: string }>(this.LRP_URL + 'base/effectivedate/latest')
  }

  removeFinalizedRequest(request: LRPRequest) {
    let url = this.servicesCommon.getUrl(`/private/lrp/${request.company}/endorsements/requests/${request.finalizedRequestId}`);
    return this.http.delete(url, {headers: this.servicesCommon.getAuthHeaders()});
  }

  runQuote(request: LRPRequest) {
    let endorsementLengthCount = '';
    if (!!request?.endorsementLengthCounts) {
      request?.endorsementLengthCounts.forEach(l => {
        endorsementLengthCount += l + ',';
      })
      if (endorsementLengthCount.length > 0) {
        endorsementLengthCount = endorsementLengthCount.substring(0, endorsementLengthCount.length - 1);
      }
    }
    let weight2 = [this.TARGET_WEIGHT_2_STEER, this.TARGET_WEIGHT_2_HEIFER, this.TARGET_WEIGHT_2_BRAHMAN, this.TARGET_WEIGHT_2_DAIRY];
    let targetWeight = 'targetWeight=';
    let commodityTypes = '';
    if (!!request?.typeCodes) {
      request?.typeCodes.forEach(l => {
        commodityTypes += l + ',';

        if (weight2.includes(l) && !!request?.targetWeight2) {
          targetWeight += l + '_' + request.targetWeight2 + ',';
        } else if ((!weight2.includes(l) && !!request?.targetWeight1) || (request?.typeCodes.length == 1)) {
          targetWeight += l + '_' + request.targetWeight1 + ',';
        }

      })
      if (commodityTypes.length > 0) {
        commodityTypes = commodityTypes.substring(0, commodityTypes.length - 1);
      }
    }
    if (!request?.targetWeight1 && !request?.targetWeight2) {
      targetWeight += -1;
    }
    let effDate = 'effectiveDate=' + (!!request.effectiveDate ? request.effectiveDate : '');
    let stateFipsCode = 'stateFipsCode=' + (!!request?.stateFipsCode ? request?.stateFipsCode : '');
    let countyFipsCode = 'countyFipsCode=' + (!!request?.countyFipsCode ? request?.countyFipsCode : '');
    let commodityCode = 'commodityCode=' + (!!request?.commodityCode ? request?.commodityCode : '');
    let typeCode = 'typeCode=' + commodityTypes;
    let numberOfHead = 'numberOfHead=' + (!!request?.numberOfHead ? request?.numberOfHead : -1);
    let length = 'length=' + endorsementLengthCount;
    let insuredShare = 'insuredShare=' + (!!request?.insuredShare ? request?.insuredShare : -1);

    let queryParams = '?'
      + effDate
      + '&' + stateFipsCode
      + '&' + countyFipsCode
      + '&' + commodityCode
      + '&' + typeCode
      + '&' + numberOfHead
      + '&' + targetWeight
      + '&' + length
      + '&' + insuredShare
    return this.servicesCommon.getAll<any>(this.LRP_URL + request.company + '/endorsements' + queryParams);

  }

  runHistoryAnalysis(request: LRPRequest) {
    let endorsementLengthCount = request.endorsementLengthCounts[0];
    let targetWeight = 'targetWeight=' + request.targetWeight1;
    let effDate = 'effectiveDate=' + (!!request.effectiveDate ? request.effectiveDate : '');
    let stateFipsCode = 'stateFipsCode=' + (!!request?.stateFipsCode ? request?.stateFipsCode : '');
    let countyFipsCode = 'countyFipsCode=' + (!!request?.countyFipsCode ? request?.countyFipsCode : '');
    let commodityCode = 'commodityCode=' + (!!request?.commodityCode ? request?.commodityCode : '');
    let typeCode = 'typeCode=' + request.typeCodes[0];
    let numberOfHead = 'numberOfHead=' + (!!request?.numberOfHead ? request?.numberOfHead : -1);
    let length = 'length=' + endorsementLengthCount;
    let insuredShare = 'insuredShare=' + (!!request?.insuredShare ? request?.insuredShare : -1);

    let queryParams = '?'
      + effDate
      + '&' + stateFipsCode
      + '&' + countyFipsCode
      + '&' + commodityCode
      + '&' + typeCode
      + '&' + numberOfHead
      + '&' + targetWeight
      + '&' + length
      + '&' + insuredShare
    return this.servicesCommon.getAll<any>(this.LRP_URL + request.company + '/endorsements/history' + queryParams);
  }


  createNewWatchList(request: LRPRequest) {
    let userId = request.userId;
    if (!request.userId) {
      userId = this.currentState.snapshot.user.id;
    }
    let url = this.servicesCommon.getUrl(`/private/lrp/${request.company}/watchlist/user/${userId}`);
    return this.http.post<LRPWatchListQuote>(url, request, {headers: this.servicesCommon.getAuthHeaders()});
  }

  deleteWatchList(id: number, company: string = 'base') {
    let url = this.servicesCommon.getUrl(`/private/lrp/${company}/watchlist/${id}`);
    return this.http.delete<LRPWatchListQuote>(url, {headers: this.servicesCommon.getAuthHeaders()});
  }

  updateWatchList(request: LRPRequest) {
    let url = this.servicesCommon.getUrl(`/private/lrp/${request.company}/watchlist`);
    return this.http.put<LRPWatchListQuote>(url, request, {headers: this.servicesCommon.getAuthHeaders()});
  }

  loadWatchlistEndorsements(userId: number, company: string = 'base',) {
    return this.servicesCommon.getAll<{
      watchListQuoteItemId: number,
      endorsements: Map<number, LRPWatchListEndorsement[]>
    }>(`/private/lrp/${company}/watchlist/endorsements/user/${userId}`);
  }

  loadWatchlistQuotes(userId: number, company: string = 'base') {
    return this.servicesCommon.getAll<LRPWatchListQuote>(`/private/lrp/${company}/watchlist/user/${userId}`);
  }

  updateDailyReportSubscription(request: LRPRequest[]) {
    let url = this.servicesCommon.getUrl(`/private/lrp/${request[0].company}/reports/daily/subscribe`);
    return this.http.post<UserDetail[]>(url, request, {headers: this.servicesCommon.getAuthHeaders()});
  }

  loadEndorsementRequests(entityId: number, startDate: string, endDate: string, company: string = 'base') {
    return this.servicesCommon.getAll<LRPFinalizedEndorsementRequest>(`/private/lrp/${company}/endorsements/requests/associations/${entityId}?startDate=${startDate}&endDate=${endDate}`);
  }

  getType(t) {
    let type = '';
    // type += t.abbrTitle;
    // type += ': ';
    switch (t.minWeight) {
      case 100:
        type += '1.00 – 5.99 cwt';
        break;
      case 600:
        type += '6.00 – 10.00 cwt';
        break;
      case 140:
        type += '1.40 – 2.60 cwt';
        break;
      case 1000:
        type += '10.00 – 16.00 cwt';
        break;
    }
    return type;
  }

  public getLRPAssociations(userId: number) {
    return this.servicesCommon.getAll<PrfAssociation>(this.LRP_URL + `base/associations/user/${userId}`)
  }

  public deleteLRPAssociation(lrpAssociation: any, company: 'hargrove' | 'aap') {
    this.adminService.deleteLRPAssociation(lrpAssociation.id, lrpAssociation.company).pipe(catchError(error => {
      return ErrorHandler.handleHttpErrorSilently(error);
    })).subscribe(response => {
      if (!response) {
        const selectedUserId = this.currentState.snapshot.admin.selectedUser.id;
        this.store.dispatch(new SelectUserAction(selectedUserId));
        if (selectedUserId == this.currentState.snapshot.user.id) {
          this.store.dispatch(new LoadUserAction(selectedUserId));
        }
      } else {
        console.error(response)
      }
    });
  }

  public saveLRPAssociation(lrpAssociations, company: 'hargrove' | 'aap') {
    let requests = [];
    const selectedUserId = this.currentState.snapshot.admin.selectedUser.id;
    lrpAssociations.forEach(lrpAssociation => {
      let request = <PRFAssociationRequest>{
        primaryName: lrpAssociation.primaryName,
        firstName: lrpAssociation.firstName,
        taxId: lrpAssociation.taxId,
        userId: selectedUserId,
        company: company,
        internalId: lrpAssociation.internalId,
        email1: lrpAssociation.email1,
        email2: lrpAssociation.email2
      };
      requests.push(request)
    });
    return this.adminService.saveNewLRPAssociation(requests).pipe(catchError(error => {
      return ErrorHandler.handleHttpErrorSilently(error);
    }));
  }

  public getAvailableCounties(companyName: 'hargrove' | 'aap'): Observable<CountyWithPolicyNumber[]> {
    const availableCounties = combineLatest([
      this.store.select(state => state.lrp.availableCounties).pipe((distinctUntilChanged(_.isEqual))),
      this.store.select(s => s.lrp.counties)
    ]).pipe(
      map(([availableCounties, allCounties]) => {
        let counties = new Set();
        let countiesArray = [];
        if (!availableCounties) {
          return [];
        }
        let result = availableCounties.map(county => {
          let foundCounty = allCounties.find(ac => ac.code + '' == county.code + '' && ac.stateKey == county.stateKey);
          let result = {...foundCounty, policyNumber: county.policyNumber} as CountyWithPolicyNumber;
          return result;
        }).sort((a, b) => a.name < b.name ? -1 : 1);
        return result;
      })
      , distinctUntilChanged(_.isEqual));
    return availableCounties;
  }
}

export function sortRequestsByCreatedDate(a: LRPFinalizedEndorsementRequest, b: LRPFinalizedEndorsementRequest) {
  let dateA = new Date(a.requestCreated);
  let dateB = new Date(b.requestCreated);
  let result = dateA.getTime() > dateB.getTime();
  return result ? -1 : 1;
}
